import React, { useState, useRef } from 'react';
import { Camera, Upload, X } from 'lucide-react';
import { useDropzone } from 'react-dropzone';
import Webcam from 'react-webcam';
import { Button } from '@/components/ui/button';

const ImageCapture = ({ onImageCapture }) => {
  const [captureMode, setCaptureMode] = useState(null); // 'upload' or 'camera'
  const [showCamera, setShowCamera] = useState(false);
  const webcamRef = useRef(null);

  const onDrop = (acceptedFiles) => {
    const file = acceptedFiles[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const base64 = e.target.result.split(',')[1];
        onImageCapture(e.target.result, base64);
      };
      reader.readAsDataURL(file);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
      'image/webp': ['.webp']
    },
    multiple: false
  });

  const capturePhoto = () => {
    const imageSrc = webcamRef.current.getScreenshot();
    const base64 = imageSrc.split(',')[1];
    onImageCapture(imageSrc, base64);
    setShowCamera(false);
  };

  if (showCamera) {
    return (
      <div className="relative w-full" data-testid="camera-capture-view">
        <div className="absolute top-4 right-4 z-10">
          <Button
            onClick={() => setShowCamera(false)}
            variant="secondary"
            size="icon"
            data-testid="close-camera-btn"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
        <Webcam
          ref={webcamRef}
          audio={false}
          screenshotFormat="image/jpeg"
          className="w-full rounded-sm"
          videoConstraints={{
            facingMode: 'user',
            width: 1280,
            height: 720
          }}
        />
        <div className="mt-6 flex justify-center">
          <Button
            onClick={capturePhoto}
            className="bg-text-primary text-bg-primary px-8 py-4 font-sans text-sm tracking-widest uppercase hover:bg-accent-primary transition-colors"
            data-testid="capture-photo-btn"
          >
            CAPTURE PHOTO
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full space-y-6" data-testid="image-capture-options">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Upload Option */}
        <div
          {...getRootProps()}
          className={`border-2 border-dashed border-border-color p-12 rounded-sm cursor-pointer transition-all hover:border-accent-primary hover:bg-bg-secondary ${
            isDragActive ? 'border-accent-primary bg-bg-secondary' : 'bg-bg-primary'
          }`}
          data-testid="upload-dropzone"
        >
          <input {...getInputProps()} data-testid="file-input" />
          <div className="flex flex-col items-center justify-center space-y-4">
            <Upload className="h-12 w-12 text-text-muted" />
            <div className="text-center">
              <p className="font-sans text-text-primary font-medium">
                {isDragActive ? 'Drop your photo here' : 'Upload Photo'}
              </p>
              <p className="font-sans text-sm text-text-muted mt-2">
                Drag & drop or click to browse
              </p>
            </div>
          </div>
        </div>

        {/* Camera Option */}
        <div
          onClick={() => setShowCamera(true)}
          className="border-2 border-dashed border-border-color p-12 rounded-sm cursor-pointer transition-all hover:border-accent-primary hover:bg-bg-secondary bg-bg-primary"
          data-testid="camera-option"
        >
          <div className="flex flex-col items-center justify-center space-y-4">
            <Camera className="h-12 w-12 text-text-muted" />
            <div className="text-center">
              <p className="font-sans text-text-primary font-medium">
                Use Camera
              </p>
              <p className="font-sans text-sm text-text-muted mt-2">
                Take a photo now
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImageCapture;